import axios from "axios";
import {createContext, useCallback, useContext, useEffect, useMemo, useState} from "react";

const ACCESS_KEY = "access";
const REFRESH_KEY = "refresh";
const TOKEN_URL = "/auth/token/";
const REFRESH_URL = "/auth/token/refresh/";

const storage = {
    get: () => ({
        access: localStorage.getItem(ACCESS_KEY),
        refresh: localStorage.getItem(REFRESH_KEY),
    }),
    set: ({access, refresh}) => {
        if (access) localStorage.setItem(ACCESS_KEY, access);
        if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
    },
    clear: () => {
        localStorage.removeItem(ACCESS_KEY);
        localStorage.removeItem(REFRESH_KEY);
    },
};

// Notified when the session can no longer be recovered (refresh failed).
let onSessionExpired = () => {};
let refreshPromise = null;

const isAuthUrl = (url = "") => url.includes(TOKEN_URL);

const refreshAccessToken = () => {
    // Share one in-flight refresh between concurrent 401s.
    if (!refreshPromise) {
        const {refresh} = storage.get();
        refreshPromise = axios
            .post(REFRESH_URL, {refresh})
            .then(({data}) => {
                // ROTATE_REFRESH_TOKENS returns a new refresh token as well.
                storage.set(data);
                return data.access;
            })
            .catch((error) => {
                storage.clear();
                onSessionExpired();
                throw error;
            })
            .finally(() => {
                refreshPromise = null;
            });
    }
    return refreshPromise;
};

axios.interceptors.request.use((config) => {
    const {access} = storage.get();
    if (access && !isAuthUrl(config.url)) {
        config.headers.Authorization = `Bearer ${access}`;
    }
    return config;
});

axios.interceptors.response.use(
    (response) => response,
    async (error) => {
        const {response, config} = error;
        const canRetry =
            response?.status === 401 && config && !config._retry && !isAuthUrl(config.url) && storage.get().refresh;

        if (!canRetry) {
            return Promise.reject(error);
        }

        config._retry = true;
        const access = await refreshAccessToken();
        config.headers.Authorization = `Bearer ${access}`;
        return axios(config);
    }
);

const AuthContext = createContext(null);

const AuthProvider = ({children}) => {
    const [tokens, setTokens] = useState(storage.get);

    useEffect(() => {
        onSessionExpired = () => setTokens({access: null, refresh: null});
        return () => {
            onSessionExpired = () => {};
        };
    }, []);

    // Keep tabs in sync (login/logout elsewhere).
    useEffect(() => {
        const onStorage = (event) => {
            if (event.key === ACCESS_KEY || event.key === REFRESH_KEY || event.key === null) {
                setTokens(storage.get());
            }
        };
        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
    }, []);

    // Throws on failure; the error carries the API response for the form.
    const login = useCallback(async (username, password) => {
        const {data} = await axios.post(TOKEN_URL, {username, password});
        storage.set(data);
        setTokens({access: data.access, refresh: data.refresh});
    }, []);

    const logout = useCallback(() => {
        storage.clear();
        setTokens({access: null, refresh: null});
    }, []);

    const contextValue = useMemo(
        () => ({
            token: tokens.access,
            isAuthenticated: Boolean(tokens.access),
            login,
            logout,
        }),
        [tokens.access, login, logout]
    );

    return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    return useContext(AuthContext);
};

export default AuthProvider;
